// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Link } from 'react-router-dom';

import { cx } from './cx';
import styles from './TextLink.module.css';

export interface TextLinkBase {
  /** `inherit` (default) takes the surrounding text's size; `sm` is the controls' size. */
  size?: 'sm' | 'inherit';
  /** Default `accent`. */
  tone?: 'accent' | 'muted';
  nowrap?: boolean;
  className?: string;
  children?: ReactNode;
}

type AnchorRest = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof TextLinkBase | 'href'>;

/** The default form: an inline `<button type="button">` action. */
export type TextLinkButtonProps = TextLinkBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof TextLinkBase> & {
    to?: never;
    href?: never;
    ref?: Ref<HTMLButtonElement>;
  };

/** A plain `<a href>` (a URL, a download) — needs no router. */
export type TextLinkAnchorProps = TextLinkBase &
  AnchorRest & { href: string; to?: never; ref?: Ref<HTMLAnchorElement> };

/** An in-app route: a react-router `Link`. */
export type TextLinkRouteProps = TextLinkBase & AnchorRest & { to: string; href?: never; ref?: Ref<HTMLAnchorElement> };

export type TextLinkProps = TextLinkButtonProps | TextLinkAnchorProps | TextLinkRouteProps;

function linkClass({ size = 'inherit', tone = 'accent', nowrap, className }: TextLinkBase): string {
  return cx(styles.link, tone === 'muted' && styles.muted, size === 'sm' && styles.sm, nowrap && styles.nowrap, className);
}

function guard(
  disabled: AnchorRest['aria-disabled'],
  onClick: AnchorRest['onClick'],
): (e: MouseEvent<HTMLAnchorElement>) => void {
  return (e) => {
    if (disabled === true || disabled === 'true') {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };
}

/**
 * An action or link that reads as text: accent, medium weight, underlined.
 * Without `to`/`href` it is a `<button type="button">`; with `href` a plain
 * `<a>`; with `to` a router `Link`. `className` and every other prop land on
 * that element. On a phone it keeps its line box and grows only its hit area.
 */
export function TextLink(props: TextLinkProps) {
  const cls = linkClass(props);
  if (props.to !== undefined) {
    const { size, tone, nowrap, className, to, href, onClick, ...rest } = props;
    return <Link to={to} {...rest} className={cls} onClick={guard(rest['aria-disabled'], onClick)} />;
  }
  if (props.href !== undefined) {
    const { size, tone, nowrap, className, to, href, onClick, ...rest } = props;
    return <a href={href} {...rest} className={cls} onClick={guard(rest['aria-disabled'], onClick)} />;
  }
  const { size, tone, nowrap, className, to, href, type = 'button', ...rest } = props;
  return <button type={type} {...rest} className={cls} />;
}
